import { useState, useEffect } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// @ts-ignore
import DateRangePicker from "react-bootstrap-daterangepicker";
// helpers
import {
  hotelPanelRootUrl,
  pagesUrl,
  removeTrailingSlash,
  shouldShowRefreshPageIcon,
  initialHotelGroup,
  getHotelName,
  startingDateRange
} from "../../common/utils/panel-helpers/helpers";
import { leaveProcessNoticeMsg } from "../../pages/Offers/offersHelpers";
// components
// @ts-ignore
import { CustomTooltip, CustomCheckbox, CustomAlertDialog } from "@gl-shared/shared/ui";
// icons
import {
  getLocalLogoIcon,
  calendarSolidIcon,
  refreshIcon,
  downArrowIcon,
  infoCircleIcon
  // @ts-ignore
} from "@gl-shared/shared/vector";
// hooks
import { useComponentVisible } from "../../common/hooks/useComponentsVisible";
// actions
import { useActions } from "../../common/hooks/useActions";
import { homeActionCreators } from "../../common/store/modules/homeModule";
import { bookingsActionCreators } from "../../common/store/modules/bookingsModule";
import { offersActionCreators } from "../../common/store/modules/offersModule";
import { globalActionCreators } from "../../common/store/modules/globalModule";

const ranges = {
  Today: [moment(), moment()],
  Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
  "Last 7 Days": [moment().subtract(6, "days"), moment()],
  "Last 30 Days": [moment().subtract(29, "days"), moment()],
  "This Month": [moment().startOf("month"), moment().endOf("month")],
  "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")]
};

const allHotelsId = 0;

interface AppHeaderTypes {
  loadLogins: (hotelId?: any) => void;
  loadMarketplaceData: (hotelId?: any) => void;
  loadEmployees: (hotelId?: any) => void;
  loadSettings: (hotelId?: any) => void;
  handleSectionLoader: (loadingState: any, sectionName: any) => void;
  timerFormMessageRef: any;
  hideFormMessage: (delay: number) => void;
}

// TBD: A few methods could be moved from component to the separate helper.ts method

const AppHeader = ({
  loadLogins,
  loadMarketplaceData,
  loadEmployees,
  loadSettings,
  handleSectionLoader,
  timerFormMessageRef,
  hideFormMessage
}: AppHeaderTypes) => {
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  // handle checkbox visibility with custom hook
  const { componentVisibleRef, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
  // dropdown hotel checkbox
  const [hideCheckbox, setHideCheckbox] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // states
  const dateRangeSelection = useSelector((state: any) => state.home.dateRangeSelection);
  const hotelName = useSelector((state: any) => state.home.hotelData?.hotelName);
  const isHotelGroupExists = useSelector((state: any) => state.home.isHotelGroupExists);
  const selectedHotels = useSelector((state: any) => state.global.selectedHotels);
  const hotelGroup = useSelector((state: any) => state.home.hotelData?.hotelGroup);
  const isFormChanged = useSelector((state: any) => state.offers.isFormChanged);

  // actions
  const setDateRangeSelection = useActions(homeActionCreators?.setDateRangeSelectionAction, []);
  const loadBookingsAction = useActions(bookingsActionCreators?.loadBookingsAction, []);
  const loadOffers = useActions(offersActionCreators?.loadOffersAction, []);
  const setSelectedHotels = useActions(globalActionCreators?.setSelectedHotelsAction, []);

  useEffect(() => {
    // "hack" for handling Hotel Checkbox onChange before hiding dropdown menu
    // @ts-ignore
    isHotelGroupExists && setHideCheckbox(isComponentVisible);
  }, [isComponentVisible]);

  // TBD: shouldShowDatepicker & isSingleHotelView constants could be a one single constant (?)

  const shouldShowDatepicker =
    location?.pathname === `/${hotelPanelRootUrl}/` ||
    location?.pathname === `/${hotelPanelRootUrl}/${pagesUrl.bookings}`;

  const isSingleHotelView = () =>
    location?.pathname !== `/${hotelPanelRootUrl}/` &&
    location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.bookings}`;

  const isNotMainView = () => {
    return (
      isSingleHotelView() &&
      location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.marketplace}` &&
      location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.employees}` &&
      location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.logins}` &&
      location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.settings}` &&
      location?.pathname !== `/${hotelPanelRootUrl}/${pagesUrl.offers}`
    );
  };

  // window pathname with removed "/" at the and
  const locationUrlWithoutSlash = removeTrailingSlash(location?.pathname);

  const shouldShowRefreshIcon: boolean = shouldShowRefreshPageIcon(location);

  const refreshOffers = (hotelId?: string) => {
    loadOffers(hotelId, handleSectionLoader);
  };

  const toggleDateRange = () => {
    setDateRangeOpen(!dateRangeOpen);
  };

  const changeDateRange = (selectedDateRange: any) => {
    setDateRangeSelection({
      startDate: selectedDateRange.startDate,
      endDate: selectedDateRange.endDate
    });

    loadBookingsAction(selectedDateRange, locationUrlWithoutSlash);
  };

  const handleApply = (event: any, picker: any) => changeDateRange(picker);

  const handleDailyReport = () => {
    // Condition for opening Alert Dialog if user wants to change screen while adding/editing HOM offer
    if (
      (location?.pathname === `/${hotelPanelRootUrl}/${pagesUrl.editOffer}` ||
        location?.pathname === `/${hotelPanelRootUrl}/${pagesUrl.newOffer}`) &&
      isFormChanged
    ) {
      setIsAlertDialogOpen(true);

      return;
    }

    changeDateRange({
      startDate: moment(),
      endDate: moment()
    });
    navigate(`/${hotelPanelRootUrl}/${pagesUrl.bookings}`);
  };

  const handleDailyReportConfirmation = () => {
    setIsAlertDialogOpen(false);
    changeDateRange({
      startDate: moment(),
      endDate: moment()
    });
    navigate(`/${hotelPanelRootUrl}/${pagesUrl.bookings}`);
  };

  const handleCloseAlertDialog = () => setIsAlertDialogOpen(false);

  /* Hotel Group Dropdown methods: */
  const handleDropdownVisibility = () => setIsComponentVisible(!isComponentVisible);

  const handleHotelCheckbox = (hotel: any) => {
    // if "All" is selected
    if (hotel?.id === allHotelsId) {
      setSelectedHotels(initialHotelGroup);
    } else {
      // Hide FormMessage component when user changes the Hotel & Clear timeout (ref is from Home.js / parent component)
      clearTimeout(timerFormMessageRef.current);
      hideFormMessage(0);

      // Handle Single View hotel (can select only 1 hotel in time)
      if (isSingleHotelView()) {
        setSelectedHotels([hotel]);
        return;
      }

      // Handle #Dashboard & #Bookings Views
      let updatedArr;
      if (selectedHotels?.some((selectedHotel: any) => selectedHotel?.id === hotel?.id)) {
        updatedArr = selectedHotels?.filter((selectedHotel: any) => selectedHotel?.id !== hotel?.id);
      } else {
        updatedArr = selectedHotels.concat(hotel);
      }

      // If user deselect last selected hotel --> preselect 'All Hotels'
      if (!updatedArr?.length) {
        setSelectedHotels(initialHotelGroup);
      } else {
        // remove "All" from selection, when user clicks on some other hotel
        const arrWithRemovedAll = updatedArr?.filter((item: any) => item?.id !== allHotelsId);
        setSelectedHotels(arrWithRemovedAll);
      }
    }
  };

  const shouldDisableCheckbox = (hotelId: number) => {
    if (isSingleHotelView() && !isNotMainView()) {
      // disable "All Hotels" checkbox
      return isSingleHotelView() && hotelId === allHotelsId;
    } else if (isNotMainView()) {
      // disable all checkboxes, except already selected one
      return selectedHotels[0]?.id !== hotelId;
    }
  };

  /* END OF Hotel Group Dropdown methods: */

  const onRefreshButtonClick = () => {
    switch (location?.pathname) {
      case `/${hotelPanelRootUrl}/${pagesUrl.dashboard}`:
        loadBookingsAction(startingDateRange, locationUrlWithoutSlash);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.bookings}`:
        loadBookingsAction(startingDateRange, locationUrlWithoutSlash);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.marketplace}`:
        loadMarketplaceData(selectedHotels[0]?.id);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.employees}`:
        loadEmployees(selectedHotels[0]?.id);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.offers}`:
        refreshOffers(selectedHotels[0]?.id);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.logins}`:
        loadLogins(selectedHotels[0]?.id);
        break;
      case `/${hotelPanelRootUrl}/${pagesUrl.settings}`:
        loadSettings(selectedHotels[0]?.id);
        break;
      default:
        return null;
    }
  };

  const onLogoClick = () => {
    navigate(`/${hotelPanelRootUrl}/${pagesUrl?.dashboard}`);
  };

  return (
    <div className="header">
      <CustomAlertDialog
        isOpen={isAlertDialogOpen}
        handleClose={handleCloseAlertDialog}
        handleBtnLeft={handleCloseAlertDialog}
        handleBtnRight={handleDailyReportConfirmation}
        title={"CHANGES NOT SAVED"}
        content={leaveProcessNoticeMsg}
        btnLeftLabel={"GO BACK"}
        btnRightLabel={"LEAVE ANYWAY"}
      />
      <div className="header__content">
        <div className="logo__area" onClick={onLogoClick}>
          <img className="hotel-logo" src={getLocalLogoIcon} alt="logo" />
        </div>
        {isHotelGroupExists ? (
          <div
            className="title__area title__area-with-dropdown"
            ref={componentVisibleRef}
            onClick={handleDropdownVisibility}
          >
            {getHotelName(selectedHotels)} <img src={downArrowIcon} alt="arrow-down" />
          </div>
        ) : (
          <div className="title__area">{hotelName}</div>
        )}
        <div className="action__area">
          {shouldShowDatepicker && (
            <DateRangePicker
              onApply={handleApply}
              ranges={ranges}
              autoApply={true}
              maxDate={moment()}
              maxSpan={{ months: 2 }}
              startDate={dateRangeSelection?.startDate}
              endDate={dateRangeSelection?.endDate}
            >
              <div className="date-range">
                <img
                  className="date-range__icon"
                  src={calendarSolidIcon}
                  onClick={toggleDateRange}
                  alt="date-range-icon"
                />
                <span onClick={toggleDateRange}>
                  {dateRangeSelection?.startDate?.format("DD.MM.YY") +
                    " - " +
                    dateRangeSelection?.endDate?.format("DD.MM.YY")}
                </span>
                {dateRangeOpen ? <div className="daterange-picker-component" /> : null}
              </div>
            </DateRangePicker>
          )}
          <div className="daily-report" onClick={handleDailyReport}>
            Daily Report
          </div>
          {shouldShowRefreshIcon && (
            <CustomTooltip title="Refresh page data" arrow>
              <div className="action__area__refresh-icon" onClick={onRefreshButtonClick}>
                <img src={refreshIcon} className="action__area__refresh-icon__img" alt="refresh-icon" />
              </div>
            </CustomTooltip>
          )}
        </div>
      </div>
      <div className="header__dropdown" style={{ display: isComponentVisible && hideCheckbox ? "flex" : "none" }}>
        {hotelGroup?.map((hotel: any) => {
          return (
            <CustomCheckbox
              key={hotel?.id}
              isChecked={selectedHotels?.find((selectedHotel: any) => selectedHotel?.id === hotel?.id) !== undefined}
              value={hotel?.id}
              onClick={() => handleHotelCheckbox(hotel)}
              label={hotel?.name}
              labelClass="header__dropdown-checkbox-label"
              isDisabled={shouldDisableCheckbox(hotel?.id)}
            />
          );
        })}
        {isSingleHotelView() && !isNotMainView() && (
          <div className="header__dropdown-info-text">
            <img src={infoCircleIcon} alt="info-btn" />
            In this view, you can only select one hotel at a time
          </div>
        )}
        {isNotMainView() && (
          <div className="header__dropdown-info-text">
            <img src={infoCircleIcon} alt="info-btn" />
            You cannot change the hotel in this view. Please navigate back to the overview.
          </div>
        )}
      </div>
    </div>
  );
};

export default AppHeader;
